import iconInfo from '@/icons/info.svg?raw'
import iconMessage from '@/icons/message.svg?raw'
import iconGoblet from '@/icons/goblet.svg?raw'
import iconGamepad from '@/icons/gamepad.svg?raw'
import { Point } from 'pixi.js'
import type { Page } from '@/scripts/data/types'
import { getIframeBaseUrl } from '@/scripts/utils/iframe'

interface GlobalEnv {
  nowDayNumber: number
  debugMode: boolean
  isArchive: boolean
  publicPath: string
  year: string
}

// Environment Variables (from build/runtime)
export const Env: GlobalEnv = {
  nowDayNumber: window.nowDayNumber || 24,
  debugMode: /true/.test(__DEBUG_MODE__ ?? 'false'),
  isArchive: !!window.isArchive,
  publicPath: __PUBLIC_PATH__ ?? '/',
  year: __YEAR__,
}

// Convenience Exports and derived constants
export const isArchive = Env.isArchive
export const debugMode = Env.debugMode
export const nowDayNumber = Env.nowDayNumber
export const projectName = `advent${Env.year}`
export const AZ_HASH = 'vanoce-a-pohadky' // TODO: Use it
export const ITEM_DELAY = 300 // TODO: Move elsewhere - ms - hover over item delay before item sound starts to play

// TODO: Reuse colors in constants.scss
export interface BulletStyle {
  FONT_BASE: string
  BULLET_NUMBER_TEXT_COLOR: string
  BULLET_NUMBER_BACKGROUND_COLOR: string
  BULLET_NUMBER_BACKGROUND_ALPHA: number
  BULLET_NUMBER_STROKE_COLOR: string
  BULLET_LABEL_TEXT_COLOR: string
  BULLET_LABEL_BACKGROUND_COLOR: string
  BULLET_LABEL_BACKGROUND_ALPHA: number
  BULLET_LABEL_STROKE_COLOR: string
  LABEL_TEXT_X_OFFSET: number
  LABEL_TEXT_X_PADDING: number
  LABEL_WIDTH: number
  LABEL_HEIGHT: number
  BULLET_RADIUS: number
  DOT_RADIUS: number
  DOT_COLOR: string
  SHOW_TWEEN_DURATION: number
}

export const style = {
  item: {
    bullet: {
      FONT_BASE: 'Playpen Sans',

      BULLET_NUMBER_TEXT_COLOR: '0XB53F26',
      BULLET_NUMBER_BACKGROUND_COLOR: '0XFFFFFF',
      BULLET_NUMBER_BACKGROUND_ALPHA: 1,
      BULLET_NUMBER_STROKE_COLOR: '0X000000',

      BULLET_LABEL_TEXT_COLOR: '0XB53F26',
      BULLET_LABEL_BACKGROUND_COLOR: '0XFFFFFF',
      BULLET_LABEL_BACKGROUND_ALPHA: 1,
      BULLET_LABEL_STROKE_COLOR: '0X000000',

      LABEL_TEXT_X_OFFSET: 25,
      LABEL_TEXT_X_PADDING: 15,
      LABEL_WIDTH: 500,
      LABEL_HEIGHT: 34,
      BULLET_RADIUS: 18,

      DOT_RADIUS: 10,
      DOT_COLOR: '#FFB3A3',

      SHOW_TWEEN_DURATION: 0.2,
    } as BulletStyle,
  },
}

export const pages: Page[] = [
  {
    id: 'o-projektu',
    title: 'O projektu',
    linkHash: 'o-projektu',
    url: `${getIframeBaseUrl()}/${projectName}/o-projektu`,
    icon: iconInfo,
    description: 'Tady se dozvíš, kdo pro tebe adventní kalendář vyrobil.',
    confirmOnClose: false,
  },
  {
    id: 'napis-nam',
    title: 'Napiš nám',
    linkHash: null,
    url: `${getIframeBaseUrl()}/${projectName}/napis-nam`,
    icon: iconMessage,
    description: 'Máš pro nás vzkaz nebo nápad? Napiš nám.',
  },
  {
    id: 'soutez',
    title: 'Soutěž',
    linkHash: 'soutez',
    url: `${getIframeBaseUrl()}/${projectName}/soutez`,
    icon: iconGoblet,
    description: 'V adventních hrách se dá taky soutěžit! Zjisti víc.',
    confirmOnClose: false,
  },
  {
    id: 'vanocni-hry',
    title: 'Vánoční hry',
    linkHash: null,
    url: `${getIframeBaseUrl()}/hry/vanocni`,
    icon: iconGamepad,
    description: 'Chceš si hrát víc? Tady najdeš další vánoční hry.',
  },
  {
    id: 'gramofon',
    title: 'Gramofon',
    linkHash: 'gramofon',
    url: `${getIframeBaseUrl()}/flashAppIframe/fdae65a7-de5e-45e8-8918-9e3093bb44b2?posterEnabled=false&closeButtonEnabled=false`,
    icon: null,
    description: 'Čtyřiadvacet vánočních písniček pro tvé ouško.',
  },
  {
    id: 'galerie',
    title: 'Galerie',
    linkHash: 'galerie',
    url: `${getIframeBaseUrl()}/flashAppIframe/18a8c9ea-fa39-4da7-aea5-8a69b92d2a64?posterEnabled=false&closeButtonEnabled=false`,
    icon: null,
    description: 'Podívej se na prastaré obrazy rentgenovýma očima.',
  },
  {
    id: 'archiv',
    title: 'Archiv',
    linkHash: null,
    url: '/adventni-kalendare',
    icon: null,
    description: 'Vzpomínáš na minulé kalendáře? Tady si můžeš brouzdat.',
  },
  {
    id: 'komiks',
    title: 'Komiks',
    linkHash: 'komiks',
    url: `${getIframeBaseUrl()}/flashAppIframe/aa7b4fc1-457e-4a8c-8998-12edb9499cca?posterEnabled=false&closeButtonEnabled=false`,
    icon: null,
    description: 'Příběh Ježíškova narození pěkně políčko po políčku.',
  },
  {
    id: 'tradicnik',
    title: 'Tradičník',
    linkHash: 'tradicnik',
    url: `${getIframeBaseUrl()}/flashAppIframe/dddfb529-0560-478d-b414-199ccd19ec8c?posterEnabled=false&closeButtonEnabled=false`,
    icon: null,
    description: 'Vysvětlíme ti kouzlo zlatých prasátek a dalších zvyků.',
  },
]

// todo add rectangle layers to PSD file and export to positionsConfig.json as sceneSize : {x:, y:}, minViewSize: {x:, y:}, viewSize: {x:, y:}}
export const dimensions = {
  // full with parallax
  SCENE_WIDTH: 2100,
  SCENE_HEIGHT: 1060,

  // always visible - scale begin
  MIN_WIDTH: 1836,
  MIN_HEIGHT: 1060, //for now should be equal to MAX_HEIGHT otherwise css container resizing doesn't work

  // full size without parallax 2:1 ratio
  MAX_WIDTH: 2010,
  MAX_HEIGHT: 1015,

  DESKTOP_BREAKPOINT: 1024,
}

// todo add rect to each parallax layer in PSD file and export to positionsConfig.json as {"name": "layer_1", size: {x: 1660, y: 830}, ...}}
export const parallaxLayerDimensions: Point[] = [
  new Point(2100 - 45, 1034), //layer 1 - far
  new Point(2100 - 34, 1038),
  new Point(2100 - 23, 1042),
  new Point(2100 - 11, 1046),
  new Point(2100, 1050), //layer 5 - (full width =  SCENE_WIDTH, SCENE_HEIGHT)
]

export const positions = {
  initialRatio: new Point(0.5, 0.5),
}

export const paths = {
  DATA_PATH: `${Env.publicPath}dat/`,
  SPRITES_PATH: `${Env.publicPath}spritesheets/`,
  IMAGES_PATH: `${Env.publicPath}images/`,
  AUDIO_SFX_PATH: `${Env.publicPath}audio/sfx/`,
  AUDIO_ITEMS_PATH: `${Env.publicPath}audio/items/`,
  AUDIO_AMBIENT_PATH: `${Env.publicPath}audio/ambient/`,
  AUDIO_MUSIC_PATH: `${Env.publicPath}audio/music/`,
  AUDIO_EXTRAS_PATH: `${Env.publicPath}audio/extras/`,
  AUDIO_TRAFFIC_PATH: `${Env.publicPath}audio/extras/traffic/`,
  AUDIO_HIDDEN_PLACES_PATH: `${Env.publicPath}audio/extras/hidden-places/`,
}

export const audio = {
  GLOBAL_VOLUME: 1,
  ITEMS_VOLUME: 1,
  HIDDEN_PLACE_REVEAL_VOLUME: 2,
  TRAFFIC_VOLUME: 0.2,
  TLESKAC_VOLUME: 0.4,
  music: [
    { name: 'hudba_1', volume: 0.2 },
    { name: 'hudba_2', volume: 0.6 },
  ],
  ambient: { name: 'atmosfera', volume: 0.4 },
}

export const animation = {
  SHOW_TWEEN_DURATION: 0.4,
}
